import React, { lazy } from 'react';
import { ITabs, ITextRulerManager, ETabType, IPaddingFrame, IRulerInfo, ITextRulerProps } from './index.interface';
import { observable, reaction } from 'mobx';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import {
    IParagraphManager,
    IUpdateProps,
} from '@src/slide/application/service/paragraph-manager/core/paragraph-manager.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { getMenuValues } from '@src/slide/application/service/menu-manager/menu-values';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { DEFAULT_PADDING_LR, DEFAULT_PADDING_TB, FONT_SCALE } from '@editor/src/page/index/components/editor/config';
import {
    IEditorInstanceService,
} from '@src/slide/application/service/editor-instance-service/editor-instance-service.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { TEXT_CHANGE_EVENT } from '@src/slide/application/service/text-ruler-manager/core/event-const';
import { CM_TO_PT } from '@src/slide/infrastructure/module/independent/unit';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { IReactionDisposer } from 'mobx/lib/internal';
import {
    TextDirectionType,
    WrapType,
} from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { getFrameAfterRotate } from '@src/slide/infrastructure/module/independent/graphic';
import { isTableModel } from '@src/slide/domain/ui-model/elements/element-judge';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { scheduleAfterFirstSlideDisplay } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import LazyBoundary from '@src/framework/frame/components/LazyBoundary';
import {
    ModelMenuRefreshEventsKeys,
} from '@src/slide/domain/ui-model/element-modeles/Interface/model-menu-refresh.interface';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';

interface ITextRulerServiceProps {
    presentation: IPresentationModel;
    paragraphManager: IParagraphManager;
    selectionService: ISelectionService;
    editorInstanceService: IEditorInstanceService;
    menuManager: IMenuManager;
}

enum EDisplayTextRulerLocalValue {
    show = '0',
    hide = '1',
}

const HORI_ROTATE_START = 113;
const HORI_ROTATE_END = 293;
const REVERT_ROTATE_START_1 = 67;
const REVERT_ROTATE_END_1 = 113;
const REVERT_ROTATE_START_2 = 247;
const REVERT_ROTATE_END_2 = 293;

// slide的默认scale
const SLIDE_SCALE_RADIO = 2;

const DISPLAY_TEXT_RULER = 'ppt_display_text_ruler';

export class TextRulerManager implements ITextRulerManager {
    @observable public defaultAlignment: ETabType = ETabType.left;
    @observable public displayRuler: boolean;
    @observable public indentLeft = 0;
    @observable public marginLeft = 0;
    @observable public tabList: Array<ITabs> = [];
    @observable public tabSize: number;
    /**
     * 水平标尺编辑区域的起始坐标，相对于标尺
     * @private
     */
    public editAreaLeft = 0;

    /**
     * 垂直标尺编辑区域的起始坐标，相对于标尺
     * @private
     */
    public editAreaTop = 0;

    /**
     * 水平标尺编辑区域的宽度
     * @private
     */
    public editAreaWidth = 0;

    /**
     * 垂直标尺编辑区域的高度
     * @private
     */
    public editAreaHeight = 0;

    /**
     * 编辑区域是否镜像翻转
     * @private
     */
    public isRevert = false;

    /**
     * 控制当前编辑区在水平标尺还是垂直标尺，同时控制制表符和按钮
     * @private
     */
    public isControlInHori = true;
    /**
     * 段落格式
     * @private
     */
    public defaultTabs: Array<number> = [];
    /**
     * 水平标尺起始坐标，相对于编辑器
     * @private
     */
    public rulerLeft = 0;
    /**
     * 垂直标尺起始坐标，相对于编辑器
     * @private
     */
    public rulerTop = 0;
    public rulerWidth = 0;
    public rulerHeight = 0;

    public paragraphManager: IParagraphManager;
    public menuManager: IMenuManager;
    public selectionService: ISelectionService;
    public editorInstanceService: IEditorInstanceService;
    private currentModel;
    private initReaction?: IReactionDisposer;
    private readonly LazyImportFileComponent = lazy(() => scheduleAfterFirstSlideDisplay(() => import(/* webpackChunkName: "lazyComponents" */ './text-ruler')));

    public constructor(props: ITextRulerServiceProps) {
        const {
            paragraphManager,
            selectionService,
            editorInstanceService,
            menuManager,
        } = props;
        this.paragraphManager = paragraphManager;
        this.selectionService = selectionService;
        this.editorInstanceService = editorInstanceService;
        this.menuManager = menuManager;

        // 页面刷新后，标尺状态依然保存
        const displayGuides = userLocalStorage.getItem(LocalStorageKeys.DISPLAY_TEXT_RULER);
        if (displayGuides === EDisplayTextRulerLocalValue.show) {
            this.displayRuler = true;
        } else {
            this.displayRuler = false;
        }

        reaction(() => [
            this.selectionService.getSelectedModel(),
            // 选区变动
            (this.selectionService.getSelectedModel() as IShapeModel).selectionStyles,
        ], ([model]) => {
            this.currentModel = model;
            this.setSelectedStyle();
        });

        // 进入编辑态
        window.addEventListener('enterEditing', () => {
            this.setSelectedStyle();
        });

        // 退出编辑态
        window.addEventListener('exitEditing', () => {
            this.setSelectedStyle();
        });

        // 文本输入监听，主要针对一些文本输入后不更新shape的情况，比如不自适应文本的形状
        window.addEventListener(TEXT_CHANGE_EVENT, () => {
            this.setSelectedStyle();
        });

        this.menuManager.event.on(ModelMenuRefreshEventsKeys.REFRESH_MENU_VALUES_VALUES, (currentModel: IBaseModel) => {
            // 监听mergeMenuValuesCacheRefresh时，就用传递过来的model,否则下方的获取当前选中model时会再次
            // new MultiplyModel，MultiplyModel在初始化时会刷新缓存，emit mergeMenuValuesCacheRefresh
            // 从而死循环了
            this.setSelectedStyle(currentModel);
        });

        if (this.displayRuler) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.TEXT_RULER,
                action: TDWActionType.EXPOSURE_TEXT_RULER,
            });
        }
    }

    public componentDidMount = (props: { callback?() }) => {
        this.initReaction = reaction(() => [
            this?.selectionService.getSelectedModel(),
            this?.defaultAlignment,
            this?.displayRuler,
            this?.indentLeft,
            this?.marginLeft,
            this?.tabList,
            this?.tabSize,
        ], () => {
            const { callback } = props || {};
            callback?.();
        });
    };

    public componentWillUnmount = () => {
        this.initReaction?.();
    };

    public getLazyComponent = (props: ITextRulerProps) => {
        const { LazyImportFileComponent } = this;
        return <LazyBoundary>
            <LazyImportFileComponent
                ref={props.setRef}
                {...props} />
        </LazyBoundary>;
    };

    /**
     * 标尺隐藏/展示
     */
    public updateDisplayRuler = () => {
        this.displayRuler = !this.displayRuler;
        if (this.displayRuler) {
            userLocalStorage.setItem(
                LocalStorageKeys.DISPLAY_TEXT_RULER,
                EDisplayTextRulerLocalValue.show,
            );
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.TEXT_RULER,
                action: TDWActionType.EXPOSURE_TEXT_RULER,
            });
        } else {
            userLocalStorage.setItem(
                LocalStorageKeys.DISPLAY_TEXT_RULER,
                EDisplayTextRulerLocalValue.hide,
            );
        }
        window.dispatchEvent(new Event('resize'));
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TEXT_RULER,
            action: TDWActionType.OPEN_TEXT_RULER,
        });
    };

    /**
     * 更新制表符插入类型
     * @param type
     */
    public updateDefaultAlignment = (type: ETabType) => {
        this.defaultAlignment = type;
    };

    /**
     * 更新制表符
     * @param tabList
     */
    public updateTabList = (tabList: Array<ITabs>) => {
        if (!this.currentModel) return;

        tabList.sort((a, b) => a.position - b.position);
        const updateProps: IUpdateProps = {
            tabList,
        };

        this.paragraphManager?.updateUIWithModel(updateProps, this.currentModel);
        this.setSelectedStyle();
    };

    /**
     * 计算编辑态下，标尺、按钮、编辑区的尺寸信息
     * @param selectedModel
     * @private
     */
    public computeEditingRulerSize = (selectedModel, scale: number) => {
        // 段落格式信息
        const {
            wrapperStyle = {},
            bodyStyle = {},
        } = selectedModel?.textBodyStyle || {};
        const {
            textDirection = TextDirectionType.HORIZONTAL,
        } = wrapperStyle;
        const { whiteSpace = WrapType.WRAP } = bodyStyle;

        // 获取段落信息
        this.getTextRulerSize();

        // 获取元素/组合子元素相对于slide的rect/frame
        const {
            shapeWidth,
            shapeHeight,
            rotate,
            flipV,
            shapeLeft,
            shapeTop,
        } = this.getElementFrame(selectedModel);

        // 计算元素旋转后的元素wrapper大小
        const shapeFrame = getFrameAfterRotate({
            left: shapeLeft,
            top: shapeTop,
            width: shapeWidth,
            height: shapeHeight,
        }, rotate);

        // 处理旋转、翻转、自动换行对标尺方向的影响
        this.setRevertAndControlAreaWithRotate(rotate);
        this.setRevertAndControlAreaWithTextDirection(textDirection);
        this.setRevertAndControlAreaWithFlip(flipV);

        // 根据worldBounds设置文本rect
        this.setEditingSizeWithYTextWorldBounds(selectedModel, scale);

        // 获取文本wrapper的rect
        let wrapperWidth = shapeFrame.width;
        let wrapperHeight = shapeFrame.height;
        let wrapperLeft = shapeFrame.left;
        let wrapperTop = shapeFrame.top;

        // 目前cell和shape的文本不是一套worldBounds，导致cell的rect需要单独处理，等editor更新后删除即可
        if (isTableModel(selectedModel)) {
            this.fixTableTextDirectionSize(selectedModel);
            const cellFrame = this.computeTableTextOffset(selectedModel, shapeFrame.left, shapeFrame.top);
            wrapperWidth = cellFrame.shapeWidth;
            wrapperHeight = cellFrame.shapeHeight;
            wrapperLeft = cellFrame.shapeLeft;
            wrapperTop = cellFrame.shapeTop;
        }

        // 非自动换行的情况下，编辑区域rect依赖父元素，而不是文本本身的rect
        this.setEditingSizeWithWrapType(whiteSpace, wrapperWidth, wrapperLeft, wrapperHeight, wrapperTop);

        // 文字编辑态下，因为文本是可能溢出画布区域的，因此标尺渲染范围受到文本区域影响
        this.fixEditingRulerSize();

        // 获取段落信息
        this.setDefaultTabs();
    };

    /**
     * 重置数据
     * @private
     */
    public resetRulerSize() {
        this.isControlInHori = true;
        this.isRevert = false;
        this.editAreaWidth = 0;
        this.editAreaHeight = 0;
        this.editAreaLeft = 0;
        this.editAreaTop = 0;
    }

    /**
     * 更新首行缩进
     * @param changeIndent
     */
    public updateIndentLeft = (changeIndent: number) => {
        if (!this.currentModel) return;

        const updateProps = {
            textIndent: changeIndent / CM_TO_PT,
        };

        this.paragraphManager?.updateUIWithModel(updateProps, this.currentModel);
        this.setSelectedStyle();
    };

    /**
     * 更新左外边距
     */
    public updateMarginLeft = (changeMargin: number) => {
        if (!this.currentModel) return;
        const updateProps = {
            marginLeft: changeMargin / CM_TO_PT,
        };

        this.paragraphManager?.updateUIWithModel(updateProps, this.currentModel);
        this.setSelectedStyle();
    };

    /**
     * 更新左悬挂（同时修改左外边距与首行缩进）
     */
    public updateMarginLeftAndIndentLeft = (props: { changeIndent: number, changeMargin: number }) => {
        if (!this.currentModel) return;
        const { changeIndent, changeMargin } = props;
        const updateProps = {
            textIndent: changeIndent / CM_TO_PT,
            marginLeft: changeMargin / CM_TO_PT,
        };

        this.paragraphManager?.updateUIWithModel(updateProps, this.currentModel);
        this.setSelectedStyle();
    };

    public getTextRect = (): { width: number, height: number} => {
        const rect = this.editorInstanceService.getEditorInstance()?.getTextAreaRectSync();
        if (!rect) {
            return {
                width: 0,
                height: 0,
            };
        }

        return rect;
    };

    /**
     * 格式化slide的2倍尺寸计算
     * @private
     */
    public initAndFormatRulerSizeWithRealScale = (info: IRulerInfo) => {
        const { slideLeft, slideWidth, slideTop, scale, slideHeight } = info;
        this.rulerLeft = slideLeft / SLIDE_SCALE_RADIO / scale;
        this.rulerWidth = slideWidth / SLIDE_SCALE_RADIO;
        this.rulerTop = slideTop / SLIDE_SCALE_RADIO / scale;
        this.rulerHeight = slideHeight / SLIDE_SCALE_RADIO;
    };

    /**
     * 计算默认制表符数组
     */
    public computeDefaultTabs = (editingLength: number): Array<number> => {
        const defaultTabs: Array<number> = [];
        if (!this.currentModel) return defaultTabs;

        // 查找最大的制表符
        let maxTabSize = 0;
        this.tabList?.forEach((tab) => {
            if (tab.position > maxTabSize) maxTabSize = tab.position;
        });

        for (let insertTabSize = this.tabSize;
            insertTabSize < editingLength;
            insertTabSize += this.tabSize) {
            // editor左边距与最大的制表符都会覆盖默认制表符
            if (insertTabSize > this.marginLeft && insertTabSize > maxTabSize) {
                defaultTabs.push(insertTabSize);
            }
        }
        return defaultTabs;
    };

    /**
     * 计算编辑态下，文本尺寸对标尺容器尺寸的影响
     * @private
     */
    private fixEditingRulerSize() {
        if (this.editAreaLeft + this.editAreaWidth > this.rulerWidth) {
            this.rulerWidth += this.editAreaLeft + this.editAreaWidth - this.rulerWidth;
        }

        if (this.editAreaTop + this.editAreaHeight > this.rulerHeight) {
            this.rulerHeight += this.editAreaTop + this.editAreaHeight - this.rulerHeight;
        }

        if (this.editAreaLeft < 0) {
            this.rulerWidth -= this.editAreaLeft;
            this.rulerLeft += this.editAreaLeft;
            this.editAreaLeft = 0;
        }

        if (this.editAreaTop < 0) {
            this.rulerHeight -= this.editAreaTop;
            this.rulerTop += this.editAreaTop;
            this.editAreaTop = 0;
        }
    }

    /**
     * 改变选区时，根据选区修改文本属性
     */
    private setSelectedStyle = (currentModel?: IBaseModel) => {
        if (!this.editorInstanceService.checkEditorIsEditing()) return;
        const model = currentModel || this.selectionService.selectedElements.getCurrentModel();
        const itemValues = getMenuValues(model);
        if (!itemValues) {
            return;
        }
        this.marginLeft = parseFloat(itemValues[ActionType.MARGIN_LEFT] ?? 0) / FONT_SCALE;
        this.indentLeft = parseFloat(itemValues[ActionType.TEXT_INDENT] ?? 0) / FONT_SCALE;
        try {
            this.tabList = itemValues[ActionType.TAB_LIST].map(tabstop => ({
                ...tabstop,
                position: tabstop.position / FONT_SCALE,
            }));
        } catch (e) {
            this.tabList = [];
        }
        this.tabSize = parseFloat(itemValues[ActionType.TAB_SIZE]) / FONT_SCALE;
    };

    private setDefaultTabs() {
        const list = this.isControlInHori ? this.editAreaWidth : this.editAreaHeight;
        this.defaultTabs = this.computeDefaultTabs(list) || [];
    }

    /**
     * 获取文本段落的标尺信息
     * @private
     */
    private getTextRulerSize() {
        this.tabList = this?.tabList || [];
        this.indentLeft = this?.indentLeft || 0;
        this.marginLeft = this?.marginLeft || 0;
    }

    private setEditingSizeWithWrapType(whiteSpace: string, wrapperWidth, wrapperLeft, wrapperHeight, wrapperTop) {
        if (whiteSpace === WrapType.WRAP) {
            if (this.isControlInHori) {
                this.editAreaWidth = wrapperWidth;
                this.editAreaLeft = wrapperLeft;
            } else {
                this.editAreaHeight = wrapperHeight;
                this.editAreaTop = wrapperTop;
            }
        }
    }

    /**
     * 计算旋转对标尺的影响
     * @param rotate
     * @private
     */
    private setRevertAndControlAreaWithRotate(rotate: number) {
        // 元素旋转后，需要标尺按钮镜像
        if (rotate > HORI_ROTATE_START && rotate < HORI_ROTATE_END) {
            this.isRevert = !this.isRevert;
        }

        // 元素旋转后，需要翻转标尺切换编辑区域
        if ((rotate > REVERT_ROTATE_START_1 && rotate < REVERT_ROTATE_END_1)
            || (rotate > REVERT_ROTATE_START_2 && rotate < REVERT_ROTATE_END_2)
        ) {
            this.isControlInHori = false;
        }
    }

    /**
     * 计算文字方向对标尺的影响
     * @param textDirection
     * @private
     */
    private setRevertAndControlAreaWithTextDirection(textDirection: TextDirectionType) {
        // 处理文字竖排/90度/270度/堆积的标尺反转
        if (textDirection !== TextDirectionType.HORIZONTAL) {
            this.isControlInHori = !this.isControlInHori;
            if (this.isControlInHori) {
                this.isRevert = !this.isRevert;
            }

            if (textDirection === TextDirectionType.ROTATE_270) {
                this.isRevert = !this.isRevert;
            }
        }
    }

    /**
     * 计算垂直翻转对标尺的影响
     * @param flipV
     * @private
     */
    private setRevertAndControlAreaWithFlip(flipV: number) {
        // 元素垂直翻转后，需要标尺按钮镜像
        if (flipV === 1) {
            this.isRevert = !this.isRevert;
        }
    }

    private setEditingSizeWithYTextWorldBounds(selectedModel, scale) {
        const worldBounds = selectedModel.editorInstance?.getRenderSize?.();
        if (!worldBounds) return;

        const { x, y, width, height } = worldBounds;
        const rulerScale = scale * 2;
        this.editAreaLeft = (x / rulerScale) - this.rulerLeft;
        this.editAreaTop = (y / rulerScale) - this.rulerTop;
        this.editAreaWidth = width / rulerScale;
        this.editAreaHeight = height / rulerScale;
    }

    /**
     * 获取元素相对于slide的内容rect
     * @param selectedModel
     * @private
     */
    private getElementFrame = (selectedModel) => {
        // 获取元素相对于slide的rect/frame
        const parseFrame = Object.assign({}, selectedModel?.props?.style || {});
        const newFrame = (selectedModel as IElementBaseModel)?.getFrame?.().frameOnSlide?.(parseFrame, false) || {};
        let shapeLeft = newFrame.left;
        let shapeWidth = newFrame.width;
        let shapeTop = newFrame.top;
        let shapeHeight = newFrame.height;
        const { flipV = 0, rotate = 0 } = newFrame;

        // 获取文本内边距，标尺编辑区域是基于内边距而不是元素rect
        const {
            paddingLeft = DEFAULT_PADDING_LR,
            paddingRight = DEFAULT_PADDING_LR,
            paddingTop = DEFAULT_PADDING_TB,
            paddingBottom = DEFAULT_PADDING_TB,
        } = this.getElementInnerPadding(selectedModel);

        shapeLeft = (shapeLeft / SLIDE_SCALE_RADIO) + paddingLeft;
        shapeWidth = (shapeWidth / SLIDE_SCALE_RADIO) - (paddingLeft + paddingRight);
        shapeTop = (shapeTop / SLIDE_SCALE_RADIO) + paddingTop;
        shapeHeight = (shapeHeight / SLIDE_SCALE_RADIO) - (paddingTop + paddingBottom);

        return {
            shapeLeft,
            shapeWidth,
            shapeTop,
            shapeHeight,
            flipV,
            rotate,
        };
    };

    /**
     * 处理表格文本的标尺偏移与尺寸
     * @param selectedModel
     * @param shapeLeft
     * @param shapeTop
     * @private
     */
    private computeTableTextOffset = (selectedModel: ITableModel, shapeLeft: number, shapeTop: number) => {
        const tableModel = selectedModel;
        const {
            colStart = 0,
            rowStart = 0,
            colEnd = 0,
            rowEnd = 0,
        } = selectedModel.selectedCellRanges[0] || {};
        const {
            paddingLeft = DEFAULT_PADDING_LR,
            paddingRight = DEFAULT_PADDING_LR,
            paddingTop = DEFAULT_PADDING_TB,
            paddingBottom = DEFAULT_PADDING_TB,
        } = this.getElementInnerPadding(selectedModel);
        const cellWidth = (selectedModel.cellWidth(colStart, colEnd - colStart + 1) / SLIDE_SCALE_RADIO)
            - (paddingLeft + paddingRight);
        const cellHeight = (selectedModel.cellHeight(rowStart, rowEnd - rowStart + 1) / SLIDE_SCALE_RADIO)
            - (paddingTop + paddingBottom);
        const newShapeLeft = (tableModel.cellWidth(0, colStart) / SLIDE_SCALE_RADIO) + shapeLeft;
        const newShapeTop = (tableModel.cellHeight(0, rowStart) / SLIDE_SCALE_RADIO) + shapeTop;
        return {
            shapeLeft: newShapeLeft,
            shapeTop: newShapeTop,
            shapeWidth: cellWidth,
            shapeHeight: cellHeight,
        };
    };

    /**
     * 处理表格独有的对齐方式
     * @param selectedModel
     * @private
     */
    // eslint-disable-next-line complexity
    private fixTableTextDirectionSize = (selectedModel: ITableModel) => {
        const shapeModel = selectedModel.getEditingCellModel();
        const wrapperStyle = shapeModel!.textBodyStyle?.wrapperStyle || {};
        const textDirection = wrapperStyle.textDirection || TextDirectionType.HORIZONTAL;
        if (textDirection !== TextDirectionType.HORIZONTAL) {
            this.isControlInHori = false;
        }

        if (textDirection === TextDirectionType.ROTATE_270) {
            this.isRevert = true;
        }
    };

    /**
     * 获取元素文本内边距
     * @param selectedModel
     * @private
     */
    private getElementInnerPadding = (selectedModel): IPaddingFrame => {
        const {
            paddingLeft = DEFAULT_PADDING_LR,
            paddingRight = DEFAULT_PADDING_LR,
            paddingTop = DEFAULT_PADDING_TB,
            paddingBottom = DEFAULT_PADDING_TB,
        } = selectedModel?.textBodyStyle?.bodyStyle || {};
        return {
            paddingLeft,
            paddingRight,
            paddingTop,
            paddingBottom,
        };
    };
}
